<template>
    <v-card class="responsive-card scrollable-card" align="center" justify="center" outlined >
        <v-card-title class="responsive-bg" :style="`background-color: ${(trade.netProfit > 0)?'#00F784':'#ff6500'}`">
            <v-list-item>
                <v-list-item-title v-if="trade.netProfit > 0">
                  <h2 class="responsive-title">{{ $t('header_FullTrade.winning') }} {{ trade.asset.ticker }}
                    <v-chip v-if="trade.automaticallyAdded" class="ml-3" color="black" text-color="white" small>
                      {{ $t('header_FullTrade.automated') }}</v-chip>
                    <v-chip v-else-if="trade.wasImported" class="ml-3" color="black" text-color="white" small>
                      {{ $t('header_FullTrade.imported') }}</v-chip>
                  </h2></v-list-item-title>
                <v-list-item-title v-else>
                  <h2 class="responsive-title" >{{ $t('header_FullTrade.losing') }} {{ trade.asset.ticker }}
                    <v-chip v-if="trade.automaticallyAdded" class="ml-2" color="black" text-color="white" small>
                      {{ $t('header_FullTrade.automated') }}</v-chip>
                    <v-chip v-else-if="trade.wasImported" class="ml-3" color="black" text-color="white" small>
                      {{ $t('header_FullTrade.imported') }}</v-chip>
                  </h2></v-list-item-title>
                <v-spacer/>
                <v-list-item-action><v-icon class="icon" @click="closeWindow" icon>fa-times</v-icon></v-list-item-action>
            </v-list-item>
        </v-card-title>
        <v-card-text class="px-7">
            <v-container fluid>
              <v-row align="center">
                <v-col class="text-left" cols="12" md="7">
                    <v-row class="mr-1">
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.asset') }}</p>
                          <p>{{ trade.asset.assetName }}</p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.account') }}</p>
                          <p>{{ trade.brokerAccount.broker.name }} - <font :style="`color: ${getAccountTypeColor(trade.brokerAccount.type)}`">{{ trade.brokerAccount.type }}</font></p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.side') }}</p>
                          <p :style="`color: ${trade.side === 'Buy'? '#008CFF' : '#FF7300'}`">{{ trade.side }}</p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.mood') }}</p>
                          <v-chip :color="getMoodColor(trade.mood)" small outlined>
                              <v-icon left>{{ getMoodIcon(trade.mood) }}</v-icon>
                              {{ getMoodText(trade.mood) }}
                          </v-chip>
                      </v-col>
                  </v-row>
                  <v-row class="mr-1" style="border-top: 1px #d2d7e1 solid;">
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.openTime') }}</p>
                          <p>{{ `${String(trade.openTime.getDate()).padStart(2,'0')}/${String(trade.openTime.getMonth() + 1).padStart(2,'0')}/${trade.openTime.getFullYear().toString().substr(-2)} ${String(trade.openTime.getHours()).padStart(2, '0')}:${String(trade.openTime.getMinutes()).padStart(2, '0')}:${String(trade.openTime.getSeconds()).padStart(2, '0')}` }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.entryPrice') }}</p>
                          <p>{{ trade.entryPrice }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.grossProfit') }}</p>
                          <p :style="`color: ${getNetProfitColor(trade.netProfit + trade.commisions)}`"><b>{{ (trade.netProfit + trade.commisions) }}</b></p>
                          <p class="label" v-if="trade.contracts !== null || trade.lots !== null">{{ $t('indicators_FullTrade.contractsLots') }}</p>
                          <p v-if="trade.contracts !== null || trade.lots !== null">{{ (trade.contracts !== null)? `${trade.contracts} ${$t('indicators_FullTrade.contracts')}` : `${trade.lots} ${$t('indicators_FullTrade.lots')}`}}</p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.closeTime') }}</p>
                          <p>{{ `${String(trade.closeTime.getDate()).padStart(2,'0')}/${String(trade.closeTime.getMonth() + 1).padStart(2,'0')}/${trade.closeTime.getFullYear().toString().substr(-2)} ${String(trade.closeTime.getHours()).padStart(2, '0')}:${String(trade.closeTime.getMinutes()).padStart(2, '0')}:${String(trade.closeTime.getSeconds()).padStart(2, '0')}` }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.closePrice') }}</p>
                          <p>{{ trade.closePrice }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.netProfit') }}</p>
                          <p :style="`color: ${getNetProfitColor(trade.netProfit)}`"><b>{{ trade.netProfit }}</b></p>
                          <p class="label" v-if="trade.mae !== null  || trade.swap !== null">{{ (trade.mae !== null) ? `${$t('indicators_FullTrade.mae')}` : `${$t('indicators_FullTrade.swap')}` }}</p>
                          <p v-if="trade.mae !== null || trade.swap !== null">{{ (trade.mae !== null)? trade.mae : trade.swap }}</p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.duration') }}</p>
                          <p>{{ getTradeTime(trade.tradeTime) }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.stopLoss') }}</p>
                          <p>{{ trade.stopLossPrice }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.commissions') }}</p>
                            <p>{{ trade.commisions }}</p>
                          <p class="label" v-if="trade.mfe !== null">{{ $t('indicators_FullTrade.mfe') }}</p>
                          <p v-if="trade.mfe !== null">{{ trade.mfe }}</p>
                      </v-col>
                      <v-col cols="6" md="3">
                          <p class="label">{{ $t('indicators_FullTrade.strategy') }}</p>
                          <p v-if="trade.strategy != null">{{ trade.strategy.name }}</p>
                          <p v-else>None</p>
                          <p class="label">{{ $t('indicators_FullTrade.pipPointValue') }}</p>
                          <p>{{ trade.pipValue.toFixed(2) }}</p>
                          <p class="label">{{ $t('indicators_FullTrade.risk') }}</p>
                          <p style="color: #c93f00">{{ (trade.riskPercentage !== null && trade.riskPercentage !== 0)? `${trade.monetaryRisk.toFixed(2)} ${trade.brokerAccount.currency} (${(trade.riskPercentage * 100).toFixed(3)}%)`: trade.monetaryRisk.toFixed(2)}}</p>
                          <p class="label" v-if="trade.overnight !== null">{{ $t('indicators_FullTrade.overnight') }}</p>
                          <p v-if="trade.overnight !== null">{{ (trade.overnight)? this.$t('yes_FullTrade') : 'No' }}</p>
                      </v-col>
                  </v-row>

                  <!-- Considerations !-->
                  <v-row class="mb-n7 mr-1" style="border-top: 1px #d2d7e1 solid;">
                    <v-autocomplete v-model="selectedConsiderations" item-text="name" item-value="id" filled small-chips background-color="rgba(0,0,0,0)"
                                    class="mt-5" :items="considerations" :label="$t('tags.label')" dense multiple outlined>
                      <template v-slot:selection="data">
                        <v-chip :color="getConsiderationColor(data.item)" text-color="white" v-bind="data.attrs" :input-value="data.selected"
                                close @click="data.select" @click:close="removeConsideration(data.item.id)" small>
                          <v-icon class="mr-2" small>{{ getConsiderationIcon(data.item) }}</v-icon> {{ data.item.name }}
                        </v-chip>
                      </template>
                      <template v-slot:item="data">
                        <template>
                          <v-list-item-icon><v-icon small>{{ getConsiderationIcon(data.item) }}</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title v-html="data.item.name"/></v-list-item-content>
                        </template>
                      </template>
                    </v-autocomplete>
                  </v-row>
                </v-col>
                <v-col style="border-left: 1px solid var(--v-darkGreen-base);" cols="12" md="5">
                  <v-row v-if="!this.$store.state.user.isPro" class="my-12" justify="center" align="center">
                    <div>
                      <v-chip color="accent" text-color="black" @click="goToPro">{{ $t('image.btnUpgrade') }}
                        <v-icon class="ml-2" color="black" small>fa-star</v-icon>
                      </v-chip>
                      <p class="my-3">{{ $t('image.ctaUpgrade') }}</p>
                    </div>
                  </v-row>
                  <v-row v-else>
                    <v-col>
                      <v-hover v-if="tradeImage.imageURL">
                        <template v-slot:default="{ hover }">
                          <v-img :src="tradeImage.imageURL" max-height="270" max-width="400">
                            <v-fade-transition v-if="saved">
                              <v-overlay :dark="false" v-if="hover" class="d-flex align-center justify-center" absolute >
                                <v-btn @click="expandImage">{{ $t('image.expand') }} <v-icon class="ml-2" elevation="0" small>fa-expand</v-icon></v-btn>
                                <image-input v-model="tradeImage">
                                  <div slot="activator">
                                    <v-btn class="mt-2" >{{ $t('image.change') }} <v-icon class="ml-2" elevation="0" small>fa-exchange-alt</v-icon></v-btn>
                                  </div>
                                </image-input>
                              </v-overlay>
                            </v-fade-transition>
                          </v-img>
                        </template>
                      </v-hover>

                      <image-input v-else v-model="tradeImage">
                        <div slot="activator">
                          <v-img  src="https://hub.swiset.com/wp-content/uploads/2021/04/Trade-Image-Upload-Swiset.png"
                            class="trade-image-upload" max-height="270" max-width="400"/>
                        </div>
                      </image-input>
                      <v-slide-x-transition>
                        <div v-if="tradeImage && !saved" class="mt-2">
                          <v-btn
                            class="primary" @click="uploadImage" :loading="saving"
                            fab  small><v-icon color="white">fa-check</v-icon></v-btn>
                          <v-btn
                            class="ml-2 red" @click="cancelUploadImage" fab
                            small><v-icon color="white">fa-times</v-icon></v-btn>
                        </div>
                      </v-slide-x-transition>
                    </v-col>
                  </v-row>

                  <!-- Trade Note -->
                  <v-row class="ml-1 mt-5" style="border-top: 1px #d2d7e1 solid;">
                        <v-col cols="11" class="d-flex align-center">
                            <v-textarea v-if="editingNote || currentNote === '' || currentNote===null" :placeholder="$t('note.placeholder')"
                                        :disabled="!editingNote" v-model="currentNote" :label="$t('note.label')" :rules="noteRules" rows="4" counter auto-grow outlined/>
                            <p v-else class="text-start" v-html="linkify(currentNote)"/>
                        </v-col>
                        <v-col cols="1" class="d-flex align-center justify-center">
                            <v-icon class="icon" @click="noteChange">{{ (editingNote)? 'fa-check' : 'fa-edit' }}</v-icon>
                            <v-icon class="icon ml-3" v-if="editingNote" @click="cancelNoteChange">fa-times</v-icon>
                        </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
        </v-card-text>

        <!-- Overlay Expand Image !-->
        <v-overlay :dark="false" v-model="overlayExpand">
            <FullTradeImage :trade-image="tradeImage.imageURL" @closeWindow="overlayExpand = false"/>
        </v-overlay>
    </v-card>
</template>

<script>
    import Moods from "../moods"
    import JournalRepository from '../../../repositories/journal';
    import ConsiderationRepository from '../../../repositories/consideration';
    import ImageInput from '../../../components/ImageInput.vue'
    import FullTradeImage from "@/views/Journal/components/FullTradeImage";

    export default {
        name: "FullTrade",
        components:{
          FullTradeImage,
          ImageInput
        },
        props: {
            trade: Object,
        },
        data() {
            return {
                blurConfig: {
                  isBlurred: this.$store.state.user.isPro,
                  opacity: 0.1,
                  filter: "blur(1.2px)",
                  transition: "all .3s linear"
                },

                considerations: this.$store.state.user.considerations,
                selectedConsiderations: [],
                editingNote: false,
                currentNote: '',
                overlayExpand: false,

                //trade image uploading
                tradeImage: {},
                saving: false,
                saved: true,

                // Rules
                noteRules: [
                    n => (n && n.length <= 140) || 'Max 140 characters',
                ],
            };
        },
        watch: {
          selectedConsiderations(newValue, oldValue) {
            if(newValue.length < oldValue.length)
              this.removeConsideration(oldValue[oldValue.length - 1]);
            else if(newValue.length > oldValue.length)
              this.assignConsiderations(newValue[newValue.length - 1]);
          },
          tradeImage: {
            handler: function() {
              if(this.tradeImage.formData){
                this.saved = false;
              }
            },
            deep: true
          }
        },
        mounted() {
            this.init();
        },
        methods: {
            expandImage() {
              this.overlayExpand = true;
            },
            cancelUploadImage(){
              this.setImage();
              setTimeout(() => this.savedTradeImage(), 100);
            },
            uploadImage() {
              this.saving = true;
              let data = {
                formData: this.tradeImage.formData,
                trade: this.trade
              };

              JournalRepository.updateTradeImage(data).then((response)=>{
                this.tradeImage.imageURL = response.data[0];
                this.trade.tradeImageUrl = response.data[1];
                setTimeout(() => this.savedTradeImage(), 1000);
              });
            },
            savedTradeImage() {
              this.saving = false;
              this.saved = true;
            },
            setImage(){
              if(this.trade.tradeImageUrl){
                JournalRepository.getTradeImage(this.trade.tradeImageUrl).then((response)=>{
                  this.tradeImage = {};
                  this.tradeImage.imageURL = response.data;
                });
              }else{
                this.tradeImage.imageURL = null;
              }
            },
            init() {
              this.currentNote = this.trade.note;
              this.selectedConsiderations = [];
              ConsiderationRepository.getConsiderationsPerTrade(this.trade.id).then((response) => {
                if(response.status < 400)
                  response.data.forEach((considerationTrade) => { this.selectedConsiderations.push(considerationTrade.consideration)});
              });
              this.setImage();
            },
            removeConsideration(consideration) {
              let considerationTrade = {
                consideration: { id: consideration },
                trade: { id: this.trade.id },
              };

              ConsiderationRepository.removeConsideration(considerationTrade).then((response) => {
                if(response.status < 400){
                  let index = this.selectedConsiderations.indexOf(consideration);
                  if (index >= 0) this.selectedConsiderations.splice(index, 1);
                }
                else alert('An error occurred :(');
              });
            },
            assignConsiderations(consideration) {
              if (this.selectedConsiderations.length > 0) {
                let considerationTrade = {
                  consideration: {id: consideration},
                  trade: {id: this.trade.id},
                };

                ConsiderationRepository.assignConsideration(considerationTrade).then((response) => {
                  if (response.status > 400)
                    alert('An error occurred :(');
                });
              }
            },
            getAccountTypeColor(type) {
                return (type.toUpperCase() === "LIVE") ? '#00a575' : '#008cff';
            },
            getTradeSideColor(side) {
                return (side.toUpperCase() === "BUY") ? '#00a575' : '#ff8800';
            },
            getNetProfitColor(netProfit) {
                return (netProfit > 0) ? '#00a575' : '#ff6b55';
            },
            getMoodColor(mood) {
                switch (mood) {
                    case 'Relaxed':
                        return "#4d6556";
                    case 'Cautious':
                        return "#386538";
                    case 'Concentrated':
                        return "#376563";
                    case 'Confident':
                        return "#376563";
                    case 'Excited':
                        return "#28655c";
                    case 'Anxious':
                        return "#28655c";
                    case 'Happy':
                        return "#0f6500";
                    case 'Optimistic':
                        return "#1d5465";
                    case 'Aggressive':
                        return "#651540";
                    case 'Angry':
                        return "#650700";
                    case 'Bored':
                        return "#65412e";
                    case 'Mad':
                        return "#651540";
                    case 'Nervous':
                        return "#651540";
                    case 'Sad':
                        return "#65412e";
                    case 'Upset':
                        return "#651540";
                    case 'None':
                        return "#555555";
                    default:
                        return "#651540";
                }
            },
            getMoodIcon(mood) {
                switch (mood) {
                    case 'Relaxed':
                        return "fa-laugh-wink";
                    case 'Cautious':
                        return "fa-smile";
                    case 'Concentrated':
                        return "fa-meh-blank";
                    case 'Confident':
                        return "fa-smile-beam";
                    case 'Excited':
                        return "fa-grin-tongue";
                    case 'Happy':
                        return "fa-grin-beam";
                    case 'Optimistic':
                        return "fa-grin-stars";
                    case 'Aggressive':
                        return "fa-meh-rolling-eyes";
                    case 'Angry':
                        return "fa-angry";
                    case 'Bored':
                        return "fa-frown";
                    case 'Mad':
                        return "fa-angry";
                    case 'Nervous':
                        return "fa-grimace";
                    case 'Sad':
                        return "fa-sad-tear";
                    case 'Upset':
                        return "fa-frown-open";
                    case 'None' :
                        return "fa-ban";
                    default:
                        return "fa-flushed";
                }
            },
            getMoodText(mood){
                let currentMood = Moods.find(cmood => cmood.en === mood);
                if(this.$i18n.locale === "en"){
                    return currentMood.en;
                }else{
                    return currentMood.es;
                }
            },
            getConsiderationColor(consideration) {
              let considerationType = consideration.type;
              return (considerationType === 'HIT')? 'var(--v-darkGreen-base)' : (considerationType === 'MISTAKE')? '#f14400' : '#3b3b3b';
            },
            getConsiderationIcon(consideration) {
              let considerationType = consideration.type;
              return (considerationType === 'HIT')? 'fa-check' : (considerationType === 'MISTAKE')? 'fa-times' : 'fa-circle-notch';
            },
            getTradeTime(duration){
                let message = (duration.days > 0)? `${duration.days}d`: '';
                message = `${message} ${(duration.hours > 0)? `${duration.hours}h`: ''}`;
                message = `${message} ${(duration.minutes > 0)? `${duration.minutes}m`: ''}`;
                message = `${message} ${(duration.seconds > 0)? `${duration.seconds}s`: ''}`;
                return message;
            },
            noteChange() {
                if(this.currentNote !== this.trade.note && this.currentNote.length <= 140 && this.editingNote) {
                    JournalRepository.updateTradeNote(this.trade.id, this.currentNote).then((response) => {
                        if(response.status < 400)
                            this.trade.note = this.currentNote;
                    });
                }
                this.editingNote = !this.editingNote;
            },
            cancelNoteChange() {
                this.currentNote = this.trade.note;
                this.editingNote = !this.editingNote;
            },
            linkify(inputText) {
                let replacedText, replacePattern1, replacePattern2;

                //URLs starting with http://, https://, or ftp://
                // eslint-disable-next-line no-useless-escape
                replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
                replacedText = inputText.replace(replacePattern1, '<a href="$1" target="_blank">$1</a>');

                //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
                // eslint-disable-next-line no-useless-escape
                replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
                replacedText = replacedText.replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>');
                return `<b>${this.$t('note.label')}:</b><br>${replacedText}`;
            },
            goToPro() {
                this.$router.push('/subscription-plans');
            },
            closeWindow() {
                this.$emit('closeShowWindow');
            },
        }
    }
</script>



<style scoped>
  .trade-image-upload{
    cursor: pointer !important;
  }

  .disable-events {
      pointer-events: none;
  }
  .responsive-card {
    max-width: 85vw;
}
.responsive-title {
    font-size: 1.5em;
}
.responsive-bg {
    width: 100%;
}
@media (max-width: 600px) {
    .responsive-card {
        max-width: 400px;
    }
    .scrollable-card {
    max-height: 400px; /* Ajusta este valor según tus necesidades */
    overflow-y: auto;
}
.responsive-title {
        font-size: 1em;
    }
    .responsive-bg {
        width: 430px;
    }
}

</style>